import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, ShieldCheck, AlertCircle, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayRequestPage() {
  const { type, id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [pollStatus, setPollStatus] = useState(sessionId ? "checking" : null);
  const pollCount = useRef(0);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pay-requests/${type}/${id}`);
      setInfo(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Payment request not found");
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async () => {
      pollCount.current += 1;
      try {
        const res = await axios.get(`${API}/pay-requests/status/${sessionId}`);
        const s = res.data.status;
        if (s === "paid" || s === "held" || s === "captured" || s === "released") {
          setPollStatus("done");
          fetchInfo();
          return;
        }
      } catch (e) { /* keep polling */ }
      if (pollCount.current < 8) {
        timer = setTimeout(poll, 2500);
      } else {
        setPollStatus("timeout");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, fetchInfo]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/pay-requests/${type}/${id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  const isDeposit = type === "deposit";
  const isComplete = info && ["paid", "held", "captured", "released"].includes(info.status);

  return (
    <div className="min-h-screen bg-[#F5F5F4] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full overflow-hidden" data-testid="pay-request-card">
        <div className="bg-[#134E4A] text-white p-6 text-center">
          <h1 className="font-serif text-2xl">Chestnut Hill Inn</h1>
          <p className="text-white/80 text-sm mt-1">
            {isDeposit ? "Security Deposit Hold" : "Payment Request"}
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-[#134E4A]" /></div>
          ) : error && !info ? (
            <div className="text-center py-6" data-testid="pay-request-error">
              <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <p className="text-stone-600">{error}</p>
            </div>
          ) : (
            <>
              {sessionId && pollStatus === "checking" && !isComplete && (
                <div className="text-center py-6" data-testid="pay-request-checking">
                  <Loader2 className="h-10 w-10 animate-spin text-[#134E4A] mx-auto mb-3" />
                  <p className="text-stone-600">Confirming with our payment provider…</p>
                </div>
              )}

              {isComplete ? (
                <div className="text-center py-6" data-testid="pay-request-success">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  {isDeposit ? (
                    <>
                      <h2 className="font-serif text-xl text-[#134E4A] mb-2">Hold Placed Successfully</h2>
                      <p className="text-stone-600 text-sm">
                        Your ${info.amount?.toLocaleString()} security deposit hold is in place. Remember — this is
                        a hold, not a charge. It will be automatically released after checkout.
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="font-serif text-xl text-[#134E4A] mb-2">Payment Received</h2>
                      <p className="text-stone-600 text-sm">Thank you, {info.guest_name}! Your payment of ${info.amount?.toLocaleString()} has been received.</p>
                    </>
                  )}
                </div>
              ) : info.status === "cancelled" || info.status === "expired" ? (
                <div className="text-center py-6" data-testid="pay-request-cancelled">
                  <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                  <p className="text-stone-600">This {isDeposit ? "hold request" : "payment request"} is no longer active. Please contact us if you have questions.</p>
                </div>
              ) : (!sessionId || pollStatus === "timeout") && (
                <div data-testid="pay-request-details">
                  {pollStatus === "timeout" && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-sm text-amber-800">
                      We couldn't confirm your payment yet. If you completed checkout, it may take a moment — refresh this page shortly. Otherwise you can try again below.
                    </div>
                  )}
                  <p className="text-stone-600 text-sm mb-4">Hello {info.guest_name}, for your stay {info.check_in} – {info.check_out}:</p>
                  <div className="bg-stone-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-stone-600 text-sm">{info.reason}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-stone-200 pt-3">
                      <span className="font-medium text-[#134E4A]">{isDeposit ? "Hold amount" : "Amount due"}</span>
                      <span className="font-bold text-2xl text-[#134E4A]" data-testid="pay-request-amount">${info.amount?.toLocaleString()}</span>
                    </div>
                  </div>
                  {isDeposit && (
                    <div className="flex gap-2 bg-teal-50 border border-teal-200 rounded p-3 mb-4 text-sm text-teal-900">
                      <ShieldCheck className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>This is a <strong>hold, not a charge</strong>. No money leaves your account, and the hold is automatically released after checkout if there's no damage or excessive cleaning.</span>
                    </div>
                  )}
                  {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
                  <Button
                    onClick={handlePay}
                    disabled={paying}
                    className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6 text-base"
                    data-testid="pay-request-pay-button"
                  >
                    {paying ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CreditCard className="h-5 w-5 mr-2" />}
                    {isDeposit ? "Place Hold Securely" : "Pay Securely"}
                  </Button>
                  <p className="text-xs text-stone-400 text-center mt-3">Processed securely by Stripe</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
